import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchAgenda } from '../adminApi';
import { useAdmin } from '../AdminContext';
import { Skeleton } from '../../ui/Skeleton';
import { today } from '../../core/format/dates';
import { useCan } from '../useCan';
import { ownOnly } from '../../core/rules/staffAccess';
import { staffList, staffName } from '../../core/rules/salonDays';
import type { RequestOptions } from '../../core/types/backend';
import type { AgendaAppointment } from '../../core/types/agenda';

/** Nu, als "HH:mm" in de salon. */
const nowTime = () => new Date().toTimeString().slice(0, 5);

/**
 * De dag van vandaag in één kolom: wie er komt, en per afspraak de logische volgende stap
 * (afrekenen na afloop, anders openen in de agenda). Geen tweede agenda: alleen vandaag.
 */
export function TodayAgenda() {
  const {
    index,
    site
  } = useAdmin();
  const viewer = useCan().viewer('agenda.view');
  const team = staffList(site.staff);
  const shared = viewer.isOwner && team.length > 1;
  const load = useCallback((opts: RequestOptions) => {
    const d = today();
    return fetchAgenda(d, d, opts);
  }, []);
  const {
    data,
    status
  } = useLatestLoad(load);
  const day = today();
  const now = nowTime();
  // Een collega: alleen haar eigen afspraken (core/rules/staffAccess).
  const list = ownOnly(data?.appointments ?? [], viewer).filter(a => a.date === day && a.outcome !== 'geannuleerd').sort((a, b) => a.time.localeCompare(b.time));
  return <section aria-labelledby="today-agenda" className="rounded-lg border border-line bg-white" data-id="element-1702" data-mp="1n21ob3sp6xli">
      <div className="flex items-center justify-between gap-4 border-b border-line px-4 py-4 sm:px-5" data-id="element-1703" data-mp="1i2ymds80l7hr">
        <h2 id="today-agenda" className="text-[15px] font-medium text-ink" data-id="element-1704" data-mp="2ygyhmpvx08yo">
          Vandaag in de salon
        </h2>
        <Link to="/be/agenda" className="inline-flex items-center gap-1 text-[13px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-1705" data-mp="2tix0wblyolqd">
          Agenda
          <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-1706" data-mp="35ker75juv2eb" />
        </Link>
      </div>

      {status === 'error' && !data ? <p className="px-4 py-6 text-[14px] text-muted sm:px-5" data-id="element-1707" data-mp="3ak16dkj8uspr">Kon de agenda niet laden. Open de agenda.</p> : !data ? <div role="status" aria-label="Agenda laden" className="space-y-2 px-4 py-4 sm:px-5" data-id="element-1708" data-mp="3fg8enjz44gav">
          <Skeleton className="h-10 w-full rounded" data-id="element-1709" data-mp="2hsrb7io4tbqt" />
          <Skeleton className="h-10 w-full rounded" data-id="element-1710" data-mp="2muyruljwpfzh" />
        </div> : list.length === 0 ? <p className="px-4 py-6 text-[14px] text-muted sm:px-5" data-id="element-1711" data-mp="2rrywqt50scp0">Geen afspraken vandaag.</p> : <ul className="divide-y divide-line" data-id="element-1712" data-mp="2wkrzxckubcyz">
          {list.map(a => <AgendaRow key={a.id} a={a} name={`${index.treatmentBySlug(a.treatmentSlug)?.name ?? a.treatmentSlug}${shared ? ` · bij ${staffName(team, a.staffId)}` : ''}`} past={a.time <= now} data-id="element-1713" data-mp="1z4upsimd9oal" />)}
        </ul>}
    </section>;
}
function AgendaRow({
  a,
  name,
  past
}: {
  a: AgendaAppointment;
  name: string;
  past: boolean;
}) {
  const settle = past && !a.saleId && a.outcome !== 'no-show';
  const label = a.status === 'aanvraag' ? 'Aanvraag' : a.saleId ? 'Afgerekend' : a.outcome === 'no-show' ? 'Niet gekomen' : null;
  return <li className="flex items-center gap-3 px-4 py-3 sm:px-5" data-id="element-1714" data-mp="1dlajp5pvdgsg">
      <span className={`w-12 shrink-0 text-[14px] tabular-nums ${past ? 'text-muted' : 'text-ink'}`} data-id="element-1715" data-mp="18uc468cpgmq9">{a.time}</span>
      <Link to={`/be/agenda?afspraak=${encodeURIComponent(a.id)}`} className="min-w-0 flex-1 rounded-sm transition-colors duration-150 ease-soft hover:text-clay" data-id="element-1716" data-mp="13uzezgyd33ki">
        <span className="block truncate text-[14px] text-ink" data-id="element-1717" data-mp="yu1q6liujba1">{a.customer.name}</span>
        <span className="block truncate text-[13px] text-muted" data-id="element-1718" data-mp="tw9y9ri7bp5u">
          {name}
          {a.customer.firstVisit ? ' · eerste bezoek' : ''}
        </span>
      </Link>
      {settle ? <Link to={`/be/kassa?afspraak=${encodeURIComponent(a.id)}`} className="shrink-0 whitespace-nowrap rounded-full bg-ink px-3.5 py-1.5 text-[13px] text-shell transition-colors duration-150 ease-soft hover:bg-clay" data-id="element-1719" data-mp="1emnn1ybflph0">
          Afrekenen
        </Link> : label && <span className="shrink-0 whitespace-nowrap rounded-full bg-sand px-2.5 py-1 text-[12px] text-ink" data-id="element-1720" data-mp="19tuj3cnsel5z">{label}</span>}
    </li>;
}